import { AppSidebar, NavbarLink } from '@/common'
import { useNavigate } from 'react-router-dom'
import UserMenu from '@/components/UserMenu'
import { useLogout } from '@/features/auth/hooks/useAuth'
import { OperatorUserSession } from '@/features/auth/api/AuthModel'
import toast from 'react-hot-toast'
import {
  areMandatoryInfoToCreateInstallationCompleted,
  useInstallations
} from '@/features/installations/hooks/useInstallations'
import { useAppState } from '@/app/hooks/useAppState'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { localStorageInstallationKey, supportEmail } from '@/shared/config/config'
import InstallationsMenu from '@/features/installations/components/InstallationsMenu'
import { useState } from 'react'
import DialogMaxInstallationsReached from '@/components/DialogMaxInstallationsReached'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import useI18n from '@/hooks/use18n'
import OperatorLanguageSelector from '@/shared/components/LanguageSelector'

export default function Navbar({ session }: { session: OperatorUserSession }) {
  const { t } = useI18n()
  const { mutateAsync: logout } = useLogout()
  const { selectedInstallationId } = useAppState()

  const organization = session.organization
  const installationsQuery = useInstallations()
  const { checkPermission } = usePermissions()
  const navigate = useNavigate()

  const installations = installationsQuery.data ?? []
  const maxNumberInstallations = session.organization.maxNumberInstallations
  const organizationName = session.organization.name
  const installationId = selectedInstallationId || installations[0]?.id
  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organization.placeOfRegistration
  )
  const isInstallationSetupComplete =
    areMandatoryInfoToCreateInstallationCompleted(
      organization,
      organization.country,
      doesOrganizationRequiresLocalAddress
    ) && installations.length > 0
  const installationName = installations.find(
    installation => installation.id === installationId
  )?.name
  const installationsOptions = installations.map(installation => ({
    id: installation.id,
    name: installation.name,
    isActive: installation.id === installationId
  }))

  const [isInstallationsMaxReachedDialogOpen, setIsInstallationsMaxReachedDialogOpen] =
    useState(false)

  const onClickDisabled = (evt: React.MouseEvent) => {
    evt.preventDefault()
    toast.error(t('common:nav.permissionDenied'), {
      position: 'bottom-center'
    })
  }

  const createNewInstallation = () => {
    if (installations.length + 1 > maxNumberInstallations) {
      setIsInstallationsMaxReachedDialogOpen(true)
      return
    }
    window.localStorage.removeItem(localStorageInstallationKey)
    navigate('/installations/new')
  }

  const canSeeMesIntegration = checkPermission('integration', 'read')

  return (
    <>
      <AppSidebar
        appLabel="Operator"
        acountSection={
          session && (
            <UserMenu
              fullName={session.firstName + ' ' + session.lastName}
              organizationName={organizationName}
              onLogout={logout}
            />
          )
        }
        languageSelector={<OperatorLanguageSelector showLabel={false} />}
      >
        <div className="space-y-8 mt-6">
          {isInstallationSetupComplete ? (
            <section className="h-1/4 flex flex-col justify-center w-full gap-2 pb-4">
              <InstallationsMenu
                installationName={installationName || ''}
                installations={installationsOptions || []}
                clickNewInstallation={() => createNewInstallation()}
              />
              <NavbarLink onClickDisabled={onClickDisabled} to={`/dashboard/${installationId}`}>
                {t('common:nav.dashboard')}
              </NavbarLink>
              <NavbarLink
                onClickDisabled={onClickDisabled}
                to={`/installations/${installationId}/materials`}
              >
                {t('common:nav.materials')}
              </NavbarLink>
            </section>
          ) : (
            <section className="h-1/4 flex flex-col w-full gap-2">
              <NavbarLink onClickDisabled={onClickDisabled} to="/installation-setup">
                {t('common:nav.onboarding')}
              </NavbarLink>
            </section>
          )}
          <section className="flex-1 space-y-2 border-t border-gray-400 pt-4">
            <NavbarLink onClickDisabled={onClickDisabled} to="/suppliers">
              {t('common:nav.suppliers')}
            </NavbarLink>
            <NavbarLink onClickDisabled={onClickDisabled} to="/importers">
              {t('common:nav.importers')}
            </NavbarLink>
            {canSeeMesIntegration && (
              <NavbarLink
                onClickDisabled={onClickDisabled}
                disabled={!isInstallationSetupComplete}
                to="/mes"
              >
                MES <span className="text-xs text-green-600">Pro</span>
              </NavbarLink>
            )}
          </section>
        </div>
      </AppSidebar>
      <DialogMaxInstallationsReached
        isOpen={isInstallationsMaxReachedDialogOpen}
        close={() => setIsInstallationsMaxReachedDialogOpen(false)}
        supportEmail={supportEmail}
      />
    </>
  )
}
